import React from "react";
import Instructions from "components/Instructions";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  formatShippingChargeOrderTotalsLabel,
} from "utils/orderTotalsChargeLabels";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";

type InstructionAttachment = {
  id?: string;
  name: string;
  sizeLabel?: string;
  previewUrl?: string;
  href?: string;
  dateLabel?: string;
};

export type SignageEditOrderInstructionsAndTotalsProps = {
  notes: string;
  onSaveInstructionsOnly: (
    newNotes: string,
    context?: { hasPendingFiles: boolean; pendingFiles?: File[] }
  ) => Promise<void>;
  attachments?: InstructionAttachment[];
  orderDocumentId?: string;
  onAfterFilesUploaded?: () => void | Promise<void>;
  token: string;
  supplierId: string;
  couponInput: string;
  appliedCouponCode?: string;
  appliedCouponAmount?: number;
  voucherApplied?: boolean;
  couponResolveDisabled?: boolean;
  couponError?: string;
  onCouponInputChange: (code: string) => void;
  onCouponApply: (code: string) => void;
  onCouponClear: () => void;
  zeroOrNegativeMsg: string | null;
  subTotalToShow: number;
  discountToShow: number;
  codeToShow: string;
  isEditingShipping: boolean;
  manualShippingAmount: string;
  onManualShippingAmountChange: (v: string) => void;
  onSaveShipping: () => void;
  onCancelEditShipping: () => void;
  onEditShipping: () => void;
  shippingAmountToShow: number;
  showShippingEditButton: boolean;
  currentSpecialFeeAmount: number;
  uiTotalTax: number;
  grandTotal: number;
  shippingOptionRows?: any[] | null;
  isUpsShipping?: boolean;
  specialFeeRows?: any[] | null;
};

/**
 * Instructions card + Order Totals (coupon, subtotal, discount, shipping edit, fees, tax, grand total) for edit flow.
 */
export function SignageEditOrderInstructionsAndTotals(props: SignageEditOrderInstructionsAndTotalsProps) {
  const {
    notes,
    onSaveInstructionsOnly,
    attachments = [],
    orderDocumentId,
    onAfterFilesUploaded,
    token,
    supplierId,
    couponInput,
    appliedCouponCode = "",
    appliedCouponAmount = 0,
    voucherApplied = false,
    couponResolveDisabled = false,
    couponError = "",
    onCouponInputChange,
    onCouponApply,
    onCouponClear,
    zeroOrNegativeMsg,
    subTotalToShow,
    discountToShow,
    codeToShow,
    isEditingShipping,
    manualShippingAmount,
    onManualShippingAmountChange,
    onSaveShipping,
    onCancelEditShipping,
    onEditShipping,
    shippingAmountToShow,
    showShippingEditButton,
    currentSpecialFeeAmount,
    uiTotalTax,
    grandTotal,
    shippingOptionRows,
    isUpsShipping,
    specialFeeRows,
  } = props;

  const shippingChargeLabel = React.useMemo(
    () => formatShippingChargeOrderTotalsLabel(shippingOptionRows, { isUps: isUpsShipping }),
    [shippingOptionRows, isUpsShipping]
  );
  const specialFeeBreakdownDisplay = React.useMemo(
    () => buildSpecialFeeCostBreakdownDisplay(specialFeeRows),
    [specialFeeRows]
  );

  return (
    <div className="row mt-3 g-4 align-items-start">
      <div className="col-12 col-lg-6" id="row-overlap">
        <div className="ms-3">
          <Instructions
            title="Instructions"
            mode="instructions_files"
            loginType={"signage"}
            instructions={notes}
            attachments={attachments}
            orderDocumentId={orderDocumentId}
            collectionName="sales"
            placeholder="Add instructions here"
            rows={4}
            onSave={onSaveInstructionsOnly}
            onAfterFilesUploaded={onAfterFilesUploaded}
            uploadFilesViaOnSave
            className="mt-3"
            iconColor="#10B981"
            iconBackground="#EAF7F1"
            headerBgColor="#eff6ff"
          />
        </div>
      </div>
      <div className="col-12 col-lg-6" id="row-overlap">
        <div className="card box-shadow-datatables shadow-border-2">
          <div className="ms-3 py-3">
            <h6 className="mb-0 order-tittle">Order Totals</h6>
          </div>
          <div className="card-body">
            <OrderDiscountCodeField
              className="mb-2"
              value={couponInput}
              appliedCode={appliedCouponCode}
              discountAmount={appliedCouponAmount}
              voucherApplied={voucherApplied}
              disabled={couponResolveDisabled}
              error={couponError}
              onChange={onCouponInputChange}
              onApply={onCouponApply}
              onClear={onCouponClear}
            />
            <hr />

            {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}

            <div className="d-flex justify-content-between py-1">
              <span className="order-card-title">Sub Total:</span>
              <span className="order-card-cells">${subTotalToShow.toFixed(2)}</span>
            </div>

            <div className="d-flex justify-content-between py-1">
              <span className="order-card-title">
                Discount{" "}
                {codeToShow ? <span style={{ color: "#00a63e" }}>({codeToShow})</span> : null}
                :
              </span>
              <span
                className="order-card-cells"
                style={{ color: discountToShow > 0 ? "#00a63e" : "#6c757d" }}
              >
                {discountToShow > 0 ? `-$${discountToShow.toFixed(2)}` : "$0.00"}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-start gap-2 py-1">
              <span className="order-card-title text-break" style={{ maxWidth: "62%" }}>
                {shippingChargeLabel}
              </span>
              {isEditingShipping ? (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "100px" }}
                    value={manualShippingAmount}
                    onChange={(e) => onManualShippingAmountChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveShipping();
                      if (e.key === "Escape") onCancelEditShipping();
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn btn-sm export-button br-8"
                    onClick={onSaveShipping}
                    title="Save shipping charge"
                    style={{ lineHeight: 1 }}
                  >
                    <i className="ri-check-line fs-14" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm table-addbutton br-8"
                    onClick={onCancelEditShipping}
                    title="Cancel"
                    style={{ lineHeight: 1 }}
                  >
                    <i className="ri-close-line fs-14" />
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <span className="order-card-cells">${shippingAmountToShow.toFixed(2)}</span>
                  {showShippingEditButton && (
                    <button
                      type="button"
                      className="btn btn-sm table-addbutton br-8"
                      onClick={onEditShipping}
                      title="Edit shipping charge"
                      style={{ lineHeight: 1 }}
                    >
                      <i className="ri-edit-line fs-14" />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-start gap-2 py-1">
              <span
                className="order-card-title text-break"
                style={{ maxWidth: "62%" }}
                title={specialFeeBreakdownDisplay.tooltip}
              >
                {specialFeeBreakdownDisplay.labelText}:
              </span>
              <span className="order-card-cells flex-shrink-0">${currentSpecialFeeAmount.toFixed(2)}</span>
            </div>

            <div className="d-flex justify-content-between py-1">
              <span>Tax :</span>
              <span className="order-card-cells">${uiTotalTax.toFixed(2)}</span>
            </div>

            <hr className="my-2" />
            <div className="d-flex justify-content-between pt-1">
              <strong className="order-card-title fs-19">Total (USD):</strong>
              <span className="order-card-cells fw-semibold">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
